import React from 'react';
interface BaseFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  className?: string;
}
export function FormSection({
  title,
  children,
  className = ''
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`mb-8 ${className}`} data-id="element-206">
      <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-207">
        {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-208">{children}</div>
    </div>;
}
export function TextField({
  label,
  error,
  required,
  className = '',
  ...props
}: BaseFieldProps & React.InputHTMLAttributes<HTMLInputElement>) {
  return <div className={className} data-id="element-209">
      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-210">
        {label} {required && <span className="text-red-500" data-id="element-211">*</span>}
      </label>
      <input className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 ${error ? 'border-red-300' : ''} ${props.disabled ? 'bg-gray-50 text-gray-500' : ''}`} {...props} data-id="element-212" />
      {error && <p className="mt-1 text-sm text-red-600" data-id="element-213">{error}</p>}
    </div>;
}
export function SelectField({
  label,
  error,
  required,
  options,
  className = '',
  ...props
}: BaseFieldProps & React.SelectHTMLAttributes<HTMLSelectElement> & {
  options: {
    value: string;
    label: string;
  }[];
}) {
  return <div className={className} data-id="element-214">
      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-215">
        {label} {required && <span className="text-red-500" data-id="element-216">*</span>}
      </label>
      <select className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 bg-white ${error ? 'border-red-300' : ''} ${props.disabled ? 'bg-gray-50 text-gray-500' : ''}`} {...props} data-id="element-217">
        <option value="" data-id="element-218">Select...</option>
        {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-219">
            {opt.label}
          </option>)}
      </select>
      {error && <p className="mt-1 text-sm text-red-600" data-id="element-220">{error}</p>}
    </div>;
}
export function TextAreaField({
  label,
  error,
  required,
  className = '',
  ...props
}: BaseFieldProps & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <div className={`md:col-span-2 ${className}`} data-id="element-221">
      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-222">
        {label} {required && <span className="text-red-500" data-id="element-223">*</span>}
      </label>
      <textarea className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2 ${error ? 'border-red-300' : ''}`} {...props} data-id="element-224" />
      {error && <p className="mt-1 text-sm text-red-600" data-id="element-225">{error}</p>}
    </div>;
}
export function DisplayField({
  label,
  value,
  className = ''
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return <div className={className} data-id="element-226">
      <label className="block text-sm font-medium text-gray-500 mb-1" data-id="element-227">
        {label}
      </label>
      <div className="text-sm text-gray-900 font-medium py-2" data-id="element-228">
        {value || '-'}
      </div>
    </div>;
}
export function FileUploadField({
  label,
  required,
  className = ''
}: BaseFieldProps) {
  return <div className={`md:col-span-2 ${className}`} data-id="element-229">
      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-230">
        {label} {required && <span className="text-red-500" data-id="element-231">*</span>}
      </label>
      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-indigo-500 transition-colors bg-gray-50" data-id="element-232">
        <div className="space-y-1 text-center" data-id="element-233">
          <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true" data-id="element-234">
            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-235" />
          </svg>
          <div className="flex text-sm text-gray-600 justify-center" data-id="element-236">
            <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 px-1" data-id="element-237">
              <span data-id="element-238">Upload a file</span>
              <input type="file" className="sr-only" multiple data-id="element-239" />
            </label>
            <p className="pl-1" data-id="element-240">or drag and drop</p>
          </div>
          <p className="text-xs text-gray-500" data-id="element-241">PNG, JPG, PDF up to 10MB</p>
        </div>
      </div>
    </div>;
}